import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, AlertTriangle, User } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_SPECIAL_CODES = ["PTO", "P", "L", "H", "A", "U"];

/**
 * Per-employee "Refresh from master" dialog. Same knobs as the facility-wide
 * ResetAllDialog but scoped to ONE employee, so a manager can:
 *   • Reset only this pay period, or fan out across N future bi-weekly periods.
 *   • Wipe everything back to the master pattern, OR tick-per-code to KEEP
 *     specific codes (PTO / LOA / U / etc.) untouched.
 */
export default function ResetEmployeeDialog({
  open, onClose, employee, payPeriodId, payPeriodLabel, codes, onDone,
}) {
  const isMonthly = String(payPeriodId || "").startsWith("mo-");

  const specials = useMemo(() => {
    if (!codes) return DEFAULT_SPECIAL_CODES.map(c => ({ code: c, label: c }));
    const nonWork = codes.filter(c => c.type && c.type !== "work");
    const merged = new Map();
    nonWork.forEach(c => merged.set(c.code, { code: c.code, label: c.label || c.code, color: c.color }));
    DEFAULT_SPECIAL_CODES.forEach(c => {
      if (!merged.has(c)) {
        const m = codes.find(x => x.code === c);
        if (m) merged.set(c, { code: c, label: m.label || c, color: m.color });
      }
    });
    if (merged.size === 0) DEFAULT_SPECIAL_CODES.forEach(c => merged.set(c, { code: c, label: c }));
    return Array.from(merged.values());
  }, [codes]);

  const [overwrite, setOverwrite] = useState({});
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("this");       // "this" | "future"
  const [periodsAhead, setPeriodsAhead] = useState(12);
  // "refresh" = apply master pattern after wiping (default)
  // "clear"   = wipe only, don't put master back on top
  const [mode, setMode] = useState("refresh");

  useEffect(() => {
    if (open) {
      setOverwrite({});
      setScope("this");
      setPeriodsAhead(12);
      setMode("refresh");
    }
  }, [open, employee?.id]);

  const toggle = (code) => setOverwrite((s) => ({ ...s, [code]: !s[code] }));
  const setAll = (val) => setOverwrite(Object.fromEntries(specials.map(s => [s.code, val])));

  const submit = async () => {
    // "unticked" = protected (kept). "ticked" = overwritten.
    const protect_codes = specials.filter(s => !overwrite[s.code]).map(s => s.code);
    if (mode === "clear") {
      const label = employee?.name || "this employee";
      if (!window.confirm(`Delete every shift for ${label} in ${payPeriodLabel}${scope === "future" && !isMonthly ? " and the next " + (Number(periodsAhead) || 0) + " pay periods" : ""}? Un-ticked codes below (${protect_codes.join(", ") || "none"}) will be kept.`)) {
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        pay_period_id: payPeriodId,
        employee_id: employee.id,
        clear_first: true,
        clear_only: mode === "clear",
        protect_codes,
        periods_ahead: scope === "future" ? Math.max(0, Number(periodsAhead) || 0) : 0,
      };
      const { data } = await api.post("/schedule/reset-employee", body);
      const verb = mode === "clear" ? "cleared" : "applied";
      const count = mode === "clear" ? data.cleared : data.applied;
      toast.success(
        `${employee.name}: ${verb} ${count} shift${count === 1 ? "" : "s"}` +
        (data.protected ? ` · kept ${data.protected}` : "") +
        (data.periods_touched > 1 ? ` across ${data.periods_touched} periods` : "")
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="reset-employee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={16} className="text-blue-600" />
            {mode === "clear" ? "Clear" : "Refresh"} {employee?.name}'s schedule
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {mode === "clear"
              ? "Delete every ticked assignment in this pay period (or future periods). Un-ticked codes below are KEPT."
              : "Overwrites this employee's assignments with their master pattern. Un-ticked codes below are KEPT."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex items-center gap-2 text-slate-700">
              <User size={14} />
              <b>{employee?.name}</b>
              <span className="text-slate-500">· {payPeriodLabel}</span>
            </div>
          </div>

          {/* Mode picker — refresh vs blank wipe */}
          <div className="space-y-2" data-testid="reset-emp-mode">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Action</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("refresh")}
                className={`rounded-md border px-3 py-2 text-sm text-left ${
                  mode === "refresh" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
                data-testid="reset-emp-mode-refresh"
              >
                <div className="font-semibold">Refresh from master</div>
                <div className="text-[11px] text-slate-500">Wipe, then apply this employee's master pattern.</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("clear")}
                className={`rounded-md border px-3 py-2 text-sm text-left ${
                  mode === "clear" ? "border-red-500 bg-red-50 text-red-800" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
                data-testid="reset-emp-mode-clear"
              >
                <div className="font-semibold">Clear all shifts</div>
                <div className="text-[11px] text-slate-500">Wipe only — leave the schedule blank.</div>
              </button>
            </div>
          </div>

          {/* Scope picker — this period vs future periods */}
          {!isMonthly && (
            <div className="space-y-2" data-testid="reset-emp-scope">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Scope</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setScope("this")}
                  className={`rounded-md border px-3 py-2 text-sm text-left ${
                    scope === "this" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                  data-testid="reset-emp-scope-this"
                >
                  <div className="font-semibold">This pay period only</div>
                  <div className="text-[11px] text-slate-500">Just the currently selected period.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setScope("future")}
                  className={`rounded-md border px-3 py-2 text-sm text-left ${
                    scope === "future" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                  data-testid="reset-emp-scope-future"
                >
                  <div className="font-semibold">Future pay periods</div>
                  <div className="text-[11px] text-slate-500">This one + the next N bi-weekly periods.</div>
                </button>
              </div>
              {scope === "future" && (
                <div className="flex items-center gap-2 pt-1">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Periods ahead:</Label>
                  <Input
                    type="number" min={0} max={52} value={periodsAhead}
                    onChange={(e) => setPeriodsAhead(e.target.value)}
                    className="w-24 tabular-nums"
                    data-testid="reset-emp-periods-input"
                  />
                  <span className="text-[11px] text-slate-500">(0 = just this one · 12 ≈ half a year · 26 ≈ 1 year)</span>
                </div>
              )}
            </div>
          )}

          {/* Special code checkboxes — un-ticked = KEEP */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                What to overwrite
              </div>
              <div className="flex items-center gap-3">
                <button type="button" className="text-[11px] text-blue-600 hover:underline" onClick={() => setAll(true)} data-testid="reset-emp-tick">
                  Tick all
                </button>
                <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setAll(false)} data-testid="reset-emp-untick">
                  Un-tick all
                </button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500">Un-ticked codes stay put. Tick them to wipe them too.</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {specials.map((s) => (
                <label
                  key={s.code}
                  className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm cursor-pointer"
                  data-testid={`reset-emp-code-${s.code}`}
                >
                  <Checkbox
                    checked={!!overwrite[s.code]}
                    onCheckedChange={() => toggle(s.code)}
                    data-testid={`reset-emp-cb-${s.code}`}
                  />
                  <span className="font-mono font-bold text-slate-700">{s.code}</span>
                  <span className="text-[11px] text-slate-500 truncate">{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className={`rounded-lg border p-3 text-[12px] flex items-start gap-2 ${
            mode === "clear" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"
          }`}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Heads up:</b>{" "}
              {mode === "clear"
                ? "ticked codes will be DELETED. Un-ticked codes (e.g. PTO) are left in place. The schedule stays blank for those days."
                : "ticked codes will be replaced with this employee's master pattern shifts. Un-ticked codes (e.g. PTO) are left in place."}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className={mode === "clear" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
            onClick={submit}
            disabled={busy}
            data-testid="reset-emp-submit"
          >
            {busy
              ? (mode === "clear" ? "Clearing…" : "Refreshing…")
              : (mode === "clear" ? "Clear schedule" : "Refresh schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
